import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Mail,
  Send,
  Paperclip,
  X,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  FileText,
  Image,
  File,
  Trash2,
  Upload,
  Shield,
  AlertCircle,
  Info
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';

// =============================================
// RICH EMAIL EDITOR WITH SPAM CHECK & ATTACHMENTS
// =============================================

// Spam check rules
const spamKeywords = [
  'gratuit', 'free', 'urgent', 'promo', 'promotion', 'offre limitée',
  'gagnez', 'winner', 'félicitations', 'congratulations', 'lottery',
  'click here', 'cliquez ici', 'act now', 'agissez maintenant',
  '100%', 'garantie', 'guarantee', 'no risk', 'sans risque',
  'argent', 'money', 'cash', '€€€', '$$$', 'bitcoin',
  'perdre du poids', 'weight loss', 'viagra', 'pharma',
  'désabonner', 'unsubscribe', 'opt-out'
];

const checkSpamScore = (subject, content) => {
  const text = `${subject} ${content}`.toLowerCase();
  let score = 0;
  const issues = [];

  // Check for spam keywords
  spamKeywords.forEach(keyword => {
    if (text.includes(keyword.toLowerCase())) {
      score += 10;
      issues.push(`Mot clé suspect: "${keyword}"`);
    }
  });

  // Check for excessive caps
  const capsRatio = (text.match(/[A-Z]/g) || []).length / text.length;
  if (capsRatio > 0.3) {
    score += 15;
    issues.push('Trop de MAJUSCULES');
  }

  // Check for excessive punctuation
  const exclamations = (text.match(/!/g) || []).length;
  if (exclamations > 3) {
    score += 10;
    issues.push(`Trop de points d'exclamation (${exclamations})`);
  }

  // Check for suspicious links count
  const linkCount = (content.match(/https?:\/\//gi) || []).length;
  if (linkCount > 5) {
    score += 15;
    issues.push(`Beaucoup de liens (${linkCount})`);
  }

  // Check subject length
  if (subject.length > 100) {
    score += 5;
    issues.push('Sujet trop long');
  }

  // Empty or very short content
  const plainText = content.replace(/<[^>]*>/g, '').trim();
  if (plainText.length < 20) {
    score += 20;
    issues.push('Contenu trop court');
  }

  // Score interpretation
  let level = 'low';
  if (score > 30) level = 'medium';
  if (score > 50) level = 'high';

  return { score, level, issues };
};

const RichEmailEditor = ({
  isOpen,
  onClose,
  recipientEmail,
  recipientName = '',
  clientId = null,
  entityType = 'client',
  onEmailSent,
  defaultSubject = '',
  defaultContent = ''
}) => {
  const [subject, setSubject] = useState(defaultSubject);
  const [content, setContent] = useState(defaultContent);
  const [attachments, setAttachments] = useState([]);
  const [clientDocuments, setClientDocuments] = useState([]);
  const [signature, setSignature] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [spamCheck, setSpamCheck] = useState({ score: 0, level: 'low', issues: [] });
  const [showDocuments, setShowDocuments] = useState(false);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const fileInputRef = useRef(null);

  // Quill modules configuration
  const modules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      ['link', 'image'],
      ['clean']
    ],
  }), []);

  // Load signature settings
  useEffect(() => {
    loadSignature();
  }, []);

  // Load client documents when modal opens
  useEffect(() => {
    if (isOpen && clientId) {
      loadClientDocuments();
    }
  }, [isOpen, clientId]);

  // Check spam score when content changes
  useEffect(() => {
    const timer = setTimeout(() => {
      const result = checkSpamScore(subject, content);
      setSpamCheck(result);
    }, 500);
    return () => clearTimeout(timer);
  }, [subject, content]);

  const loadSignature = async () => {
    try {
      const res = await api.get('/email/settings/signature');
      setSignature(res.data);
    } catch (error) {
      console.error('Error loading signature:', error);
    }
  };

  const loadClientDocuments = async () => {
    if (!clientId) return;
    setLoadingDocs(true);
    try {
      // Get client's projects
      const projectsRes = await api.get('/projects/', { params: { client_id: clientId } });
      const projects = projectsRes.data || [];
      
      // Get files from projects
      const docs = [];
      for (const project of projects.slice(0, 5)) {
        try {
          const filesRes = await api.get(`/projects/${project.id}/files`);
          const files = filesRes.data || [];
          files.forEach(file => {
            docs.push({
              ...file,
              project_name: project.name
            });
          });
        } catch (e) {}
      }
      setClientDocuments(docs);
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setLoadingDocs(false);
    }
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const newAttachments = files.map(file => ({
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      isClientDoc: false
    }));
    setAttachments([...attachments, ...newAttachments]);
  };

  const addClientDocument = (doc) => {
    // Check if already added
    if (attachments.find(a => a.id === doc.id)) {
      toast.info('Document déjà ajouté');
      return;
    }
    setAttachments([...attachments, {
      id: doc.id,
      name: doc.filename || doc.name,
      size: doc.size,
      url: doc.file_url || doc.url,
      isClientDoc: true,
      project_name: doc.project_name
    }]);
    setShowDocuments(false);
  };

  const removeAttachment = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleSendEmail = async () => {
    if (!subject.trim()) {
      toast.error('Veuillez saisir un sujet');
      return;
    }
    if (!content.trim() || content === '<p><br></p>') {
      toast.error('Veuillez saisir un message');
      return;
    }

    // Warn about high spam score
    if (spamCheck.level === 'high') {
      if (!window.confirm('Cet email a un risque élevé d\'être considéré comme spam. Continuer ?')) {
        return;
      }
    }

    setSendingEmail(true);
    try {
      // Prepare form data for attachments
      const formData = new FormData();
      formData.append('to', recipientEmail);
      formData.append('subject', subject);
      formData.append('content', content);
      formData.append('client_id', clientId || '');
      formData.append('save_to_history', 'true');

      // Add file attachments
      attachments.filter(a => !a.isClientDoc && a.file).forEach(att => {
        formData.append('attachments', att.file);
      });

      // Add client document URLs
      const docUrls = attachments.filter(a => a.isClientDoc).map(a => a.url);
      formData.append('document_urls', JSON.stringify(docUrls));

      await api.post('/email/send-rich', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Email envoyé');
      onClose();
      if (onEmailSent) onEmailSent();

      // Reset form
      setSubject('');
      setContent('');
      setAttachments([]);
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSendingEmail(false);
    }
  };

  const getSpamBadge = () => {
    if (spamCheck.level === 'low') {
      return (
        <Badge className="bg-green-100 text-green-700 gap-1">
          <Shield className="w-3 h-3" />
          Risque faible
        </Badge>
      );
    }
    if (spamCheck.level === 'medium') {
      return (
        <Badge className="bg-amber-100 text-amber-700 gap-1">
          <AlertCircle className="w-3 h-3" />
          Risque moyen
        </Badge>
      );
    }
    return (
      <Badge className="bg-red-100 text-red-700 gap-1">
        <AlertTriangle className="w-3 h-3" />
        Risque élevé
      </Badge>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] sm:w-full sm:max-w-3xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Mail className="w-5 h-5 text-blue-500" />
            <span className="truncate">Envoyer un email à {recipientName || recipientEmail}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 sm:space-y-4">
          {/* Recipient */}
          <div>
            <Label className="text-sm">Destinataire</Label>
            <Input value={recipientEmail} disabled className="bg-slate-50 mt-1 text-sm" />
          </div>

          {/* Subject */}
          <div>
            <Label className="text-sm">Sujet *</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Objet de l'email..."
              className="mt-1 text-sm"
            />
          </div>

          {/* Spam check indicator */}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="text-xs sm:text-sm text-slate-500">Analyse anti-spam :</span>
              {getSpamBadge()}
            </div>
            {spamCheck.issues.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-7"
                onClick={() => toast.info(spamCheck.issues.join('\n'), { duration: 5000 })}
              >
                <Info className="w-4 h-4 mr-1" />
                Voir ({spamCheck.issues.length})
              </Button>
            )}
          </div>

          {/* Rich text editor */}
          <div>
            <Label className="text-sm">Message *</Label>
            <div className="mt-1 border rounded-lg overflow-hidden">
              <ReactQuill
                theme="snow"
                value={content}
                onChange={setContent}
                modules={modules}
                placeholder="Rédigez votre message..."
                className="quill-mobile-responsive"
                style={{ minHeight: window.innerWidth < 640 ? '200px' : '350px' }}
              />
            </div>
          </div>

          {/* Signature preview */}
          {signature && (
            <div className="p-3 bg-slate-50 rounded-lg border">
              <p className="text-xs text-slate-500 mb-2">Signature automatique :</p>
              <div className="text-sm text-slate-600">
                <p className="font-medium">{signature.default_signature_name}</p>
                {signature.default_signature_email && (
                  <p>{signature.default_signature_email}</p>
                )}
                {signature.default_signature_phone && (
                  <p>{signature.default_signature_phone}</p>
                )}
                {signature.footer_text && (
                  <p className="text-xs text-slate-400 mt-1 italic">{signature.footer_text}</p>
                )}
              </div>
            </div>
          )}

          {/* Attachments section */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Pièces jointes</Label>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="w-4 h-4 mr-1" />
                  Ajouter un fichier
                </Button>
                {clientId && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowDocuments(true)}
                  >
                    <FileText className="w-4 h-4 mr-1" />
                    Documents client
                  </Button>
                )}
              </div>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileUpload}
              className="hidden"
            />

            {attachments.length > 0 && (
              <div className="space-y-2 p-3 bg-slate-50 rounded-lg">
                {attachments.map((att, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-2 bg-white rounded border"
                  >
                    <div className="flex items-center gap-2">
                      {att.type?.startsWith('image/') ? (
                        <Image className="w-4 h-4 text-blue-500" />
                      ) : (
                        <File className="w-4 h-4 text-slate-500" />
                      )}
                      <div>
                        <p className="text-sm font-medium truncate max-w-[300px]">{att.name}</p>
                        <p className="text-xs text-slate-400">
                          {att.size ? formatFileSize(att.size) : ''}
                          {att.isClientDoc && (
                            <Badge variant="outline" className="ml-2 text-xs">
                              {att.project_name}
                            </Badge>
                          )}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeAttachment(index)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Spam warning */}
          {spamCheck.level === 'high' && (
            <Alert className="bg-red-50 border-red-200">
              <AlertTriangle className="w-4 h-4 text-red-600" />
              <AlertDescription className="text-red-700">
                <strong>Attention :</strong> Cet email risque d'être marqué comme spam. 
                Vérifiez le contenu et évitez les mots promotionnels.
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter className="flex items-center justify-between">
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button
            onClick={handleSendEmail}
            disabled={sendingEmail || !subject.trim()}
          >
            {sendingEmail ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Envoi...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Envoyer
              </>
            )}
          </Button>
        </DialogFooter>

        {/* Client Documents Dialog */}
        <Dialog open={showDocuments} onOpenChange={setShowDocuments}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Documents du client</DialogTitle>
            </DialogHeader>
            {loadingDocs ? (
              <div className="text-center py-8">
                <Loader2 className="w-6 h-6 mx-auto animate-spin" />
              </div>
            ) : clientDocuments.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                Aucun document disponible
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {clientDocuments.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between p-2 border rounded hover:bg-slate-50 cursor-pointer"
                    onClick={() => addClientDocument(doc)}
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-slate-400" />
                      <div>
                        <p className="text-sm font-medium">{doc.filename || doc.name}</p>
                        <p className="text-xs text-slate-400">{doc.project_name}</p>
                      </div>
                    </div>
                    <Paperclip className="w-4 h-4 text-slate-400" />
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

export default RichEmailEditor;
